import { BitkitAlert, BitkitAlertProps } from '@bitrise/bitkit-v2';
import { Text } from '@chakra-ui/react/text';

import GitProvider, { getGitProviderName } from '@/models/GitProvider';
import { workspaceSettingsIntegrationsPath } from '@/utils/monolithApiRouteService';

type Props = Omit<BitkitAlertProps, 'variant' | 'messageText'> & {
  workspaceSlug: string;
  providerType?: string;
  host?: string;
};

// Hosted providers store the user's OAuth identity at the user level (one row per user
// in /me/account/connected-accounts). Self-hosted providers store it per workspace via
// OmniauthApp, so re-auth has to happen from the workspace's Integrations page.
const HOSTED_PROVIDERS = new Set<string>([
  GitProvider.GITHUB,
  GitProvider.GITHUB_APP,
  GitProvider.GITLAB,
  GitProvider.BITBUCKET,
]);

const reauthHref = (workspaceSlug: string, providerType?: string) => {
  if (providerType && HOSTED_PROVIDERS.has(providerType)) {
    return '/me/account/connected-accounts';
  }
  return workspaceSettingsIntegrationsPath(workspaceSlug, providerType);
};

const ProviderReauthRequiredAlert = ({
  titleText = 'Reconnect required',
  workspaceSlug,
  providerType,
  host,
  ...props
}: Props) => {
  const providerLabel = getGitProviderName(providerType as GitProvider | undefined);
  const integrationHref = reauthHref(workspaceSlug, providerType);
  const isHosted = providerType ? HOSTED_PROVIDERS.has(providerType) : false;

  return (
    <BitkitAlert
      variant="critical"
      action={{ label: 'Reconnect', href: integrationHref }}
      {...props}
      titleText={titleText}
      messageText={
        <>
          Your {providerLabel} connection
          {host ? (
            <>
              {' to '}
              <Text as="span" fontWeight="700">
                {host}
              </Text>
            </>
          ) : null}{' '}
          has stopped working. Reconnect from {isHosted ? 'your connected accounts' : 'Workspace Integrations'} to
          continue.
        </>
      }
    />
  );
};

export default ProviderReauthRequiredAlert;
